import { makeAutoObservable, runInAction } from "mobx";
import { agent } from "../api/agent";
import { toast } from "react-toastify";
import {
    AvailableCourtSlotDTO,
    AvailableCourtSlotsRequestDTO,
    AvailableCourtSlotsResponseDTO,
} from "../models/AvailableCourtSlots";
import { Pagination, PagingParams } from "../models/Pagination";
import i18n from "../../i18n";

export default class CourtStore {
    courtSlots: AvailableCourtSlotDTO[] = [];
    loadingCourts = false;
    loadingInitial = false;

    pagination: Pagination | null = null;
    pagingParams = new PagingParams();

    sportID: string | null = null;
    searchDate: Date = new Date();
    radiusKm: number = 50;
    sortField: string | undefined = undefined;
    sortDirection: string = "asc";

    constructor() {
        makeAutoObservable(this);
    }

    setPagingParams = (pagingParams: PagingParams) => {
        this.pagingParams = pagingParams;
    };

    setPagination = (pagination: Pagination) => {
        this.pagination = pagination;
    };

    setSportID = (id: string | null) => { this.sportID = id; };
    setSearchDate = (date: Date) => { this.searchDate = date; };
    setRadiusKm = (km: number) => { this.radiusKm = km; };
    setSortField = (field: string | undefined) => { this.sortField = field; };
    setSortDirection = (dir: string) => { this.sortDirection = dir; };

    reset = () => {
        this.courtSlots = [];
        this.pagination = null;
        this.pagingParams = new PagingParams();
        this.sportID = null;
        this.searchDate = new Date();
        this.radiusKm = 50;
        this.sortField = undefined;
        this.sortDirection = "asc";
    };

    private buildRequest = (
        userLat: number,
        userLng: number
    ): AvailableCourtSlotsRequestDTO => {
        // Construct local midnight as a UTC ISO string.
        // For Belgrade (UTC+1), March 4 local midnight = 2026-03-03T23:00:00Z
        const d = this.searchDate;
        const localMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    
        const request: AvailableCourtSlotsRequestDTO = {
            searchDate:       localMidnight.toISOString(),     // UTC ISO string
            utcOffsetMinutes: -new Date().getTimezoneOffset(), // +60 for UTC+1
            latitude:         userLat,
            longitude:        userLng,
            radiusKm:         this.radiusKm,
            pageNumber:       this.pagingParams.pageNumber,
            pageSize:         this.pagingParams.pageSize,
            sortField:        this.sortField,
            sortDirection:    this.sortDirection,
        };
        if (this.sportID) {
            request.sportID = this.sportID;
        }
        return request;
    };

    private formatDate = (date: Date): string =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
            date.getDate()
        ).padStart(2, "0")}`;

    loadCourts = async (userLat: number, userLng: number, replace = false) => {
        if (replace) {
            this.pagingParams = new PagingParams();
            runInAction(() => { this.courtSlots = []; });
        }

        this.loadingInitial = replace;
        this.loadingCourts = true;

        try {
            const request = this.buildRequest(userLat, userLng);
            const response = await agent.Courts.searchAvailableSlots(request);

            runInAction(() => {
                this.courtSlots = replace
                    ? response.availableSlots
                    : [...this.courtSlots, ...response.availableSlots];

                this.pagination = {
                    currentPage:  response.currentPage,
                    totalPages:   response.totalPages,
                    totalItems:   response.totalCount,
                    itemsPerPage: response.pageSize,
                };

                this.loadingCourts = false;
                this.loadingInitial = false;
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this.loadingCourts = false;
                this.loadingInitial = false;
            });
            toast.error((i18n as any).t("courts.errors.loadFailed"));
        }
    };
}